<template>
  <Dialog scrollable :common-dialog="visitDialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div
        v-if="visitDialog"
        class="d-flex align-center justify-space-between w-100 px-2 py-1"
      >
        <div class="d-flex align-center flex-wrap gap-2">
          <!-- White square document badge -->
          <div
            class="rounded-lg bg-white pa-2 border-slate d-flex align-center justify-center mr-2 shadow-sm"
          >
            <v-icon color="cyan darken-2" medium
              >mdi-file-document-outline</v-icon
            >
          </div>

          <h1
            class="form-title custom-nowrap-ellipsis text-capitalize mb-0 cursor-pointer font-weight-700 color-custom-blue font-size-22 mr-2"
          >
            Contract #{{ visitDetail.barcode }}
          </h1>

          <template v-if="visitDetail.status">
            <CustomStatus
              :status="visitDetail.status"
              endpoint="contract/status"
              class="mr-2"
            ></CustomStatus>
          </template>
        </div>

        <!-- Action Buttons -->
        <div v-if="!pageLoading" class="d-flex align-center gap-2">
          <!-- Revise Button -->
          <template v-if="visitDetail.status == 3 && !visitDetail.is_revised">
            <v-btn
              :disabled="statusLoading"
              class="custom-bold-button text-white rounded-lg px-3"
              color="cyan darken-2"
              depressed
              height="36"
              v-on:click="updateMoreAction('revise')"
            >
              <v-icon small left>mdi-update</v-icon> REVISE
            </v-btn>
          </template>

          <!-- Edit Button -->
          <template
            v-if="
              visitDetail.status != 7 &&
              visitDetail.status != 6 &&
              visitDetail.status != 3 &&
              visitDetail.status != 2 &&
              visitDetail.status != 5 &&
              visitDetail.status != 8
            "
          >
            <v-btn
              :disabled="statusLoading"
              class="custom-bold-button text-white rounded-lg px-4"
              color="cyan darken-1"
              depressed
              height="36"
              v-on:click="updateMoreAction('edit')"
            >
              <v-icon small left>mdi-pencil</v-icon> EDIT
            </v-btn>
          </template>

          <!-- More Actions Menu -->
          <v-menu
            v-if="moreActions && moreActions.length"
            transition="slide-y-transition"
            bottom
            content-class="custom-menu-list"
            offset-y
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :disabled="statusLoading"
                class="custom-bold-button text-white rounded-lg px-3"
                color="cyan darken-2"
                depressed
                height="36"
                v-bind="attrs"
                v-on="on"
              >
                MORE... <v-icon small right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list class="pa-1 rounded-lg">
              <template v-for="(more, index) in moreActions">
                <v-list-item
                  link
                  v-on:click="updateMoreAction(more.action)"
                  :key="index"
                  :disabled="statusLoading || more.disabled"
                >
                  <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                    <v-icon class="icon-default-blue" small>{{
                      more.icon
                    }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="font-weight-600 font-size-14">{{
                    more.title
                  }}</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>

          <!-- Close Button -->
          <v-btn
            outlined
            color="grey darken-2"
            class="custom-bold-button bg-white rounded-lg px-4"
            height="36"
            v-on:click="$emit('close', true)"
          >
            <v-icon small left>mdi-close</v-icon>
            CLOSE
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Main Modal Body Content (Matches Single-Visit-Detail Layout) -->
    <template v-slot:body>
      <div class="pa-4">
        <!-- Customer Link Banner -->
        <div v-if="customer.customer" class="mb-3">
          <router-link
            :to="
              getDefaultRoute('customer.detail', {
                params: {
                  id: customer.customer,
                },
              })
            "
            class="font-weight-700 font-size-18 color-custom-blue hover-cyan text-decoration-none"
          >
            {{ customer.display_name }}
          </router-link>
          <span
            v-if="visitDetail && visitDetail.job_title"
            class="ml-2 font-weight-600 font-size-15 text-muted"
          >
            - {{ visitDetail.job_title }}
          </span>
        </div>

        <!-- Top Split Card (Left White Half & Right Soft Blue Half) -->
        <v-card
          flat
          outlined
          class="rounded-lg overflow-hidden pl-4 mb-4 border-slate shadow-sm"
        >
          <v-row no-gutters>
            <!-- Left Half: Service Location & Billing Address (White Background) -->
            <v-col cols="12" md="7" class="bg-white pa-4 border-right-slate">
              <v-row>
                <!-- Service Location Column -->
                <v-col cols="12" sm="6" class="pr-sm-3 border-right-dashed-sm">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="32" color="cyan lighten-5" class="mr-2">
                      <v-icon small color="cyan darken-2"
                        >mdi-map-marker</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-700 font-size-15 color-custom-blue"
                      >Service Location</span
                    >
                  </div>

                  <a
                    v-if="property.property_address"
                    target="_blank"
                    :href="`https://maps.google.com/?q=${property.property_address}`"
                    class="font-weight-600 font-size-14 color-custom-blue text-decoration-none d-block mb-3 hover-cyan"
                  >
                    {{ property.property_address }}
                  </a>
                  <em v-else class="text-muted font-size-14 italic d-block mb-3"
                    >No Service Address</em
                  >

                  <v-divider class="my-3 border-dashed"></v-divider>

                  <div class="d-flex align-center mb-2">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2">mdi-account</v-icon>
                    </v-avatar>
                    <span class="font-weight-700 font-size-14 text-slate-800"
                      >Contact details</span
                    >
                  </div>

                  <template v-if="visitDetail.property_person">
                    <div
                      v-if="visitDetail.property_person.display_name"
                      class="font-size-14 text-slate-700 font-weight-500 mb-1"
                    >
                      {{ visitDetail.property_person.display_name }}
                    </div>
                    <div
                      v-if="visitDetail.property_person.primary_phone"
                      class="font-size-14 text-slate-700 d-flex align-center mb-1"
                    >
                      <v-icon x-small color="cyan darken-2" class="mr-2"
                        >mdi-phone</v-icon
                      >
                      <a
                        :href="`tel:${visitDetail.property_person.primary_phone}`"
                        class="text-slate-700 text-decoration-none"
                      >
                        {{ visitDetail.property_person.primary_phone }}
                      </a>
                    </div>
                    <div
                      v-if="visitDetail.property_person.primary_email"
                      class="font-size-14 text-slate-700 d-flex align-center"
                    >
                      <v-icon x-small color="cyan darken-2" class="mr-2"
                        >mdi-email-outline</v-icon
                      >
                      <a
                        :href="`mailto:${visitDetail.property_person.primary_email}`"
                        class="text-slate-700 text-decoration-none"
                      >
                        {{ visitDetail.property_person.primary_email }}
                      </a>
                    </div>
                  </template>
                  <em v-else class="text-muted font-size-14 italic"
                    >No Contact Details</em
                  >
                </v-col>

                <!-- Billing Address Column -->
                <v-col cols="12" sm="6" class="pl-sm-3">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="32" color="green lighten-5" class="mr-2">
                      <v-icon small color="green darken-2"
                        >mdi-file-document-outline</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-700 font-size-15 color-custom-blue"
                      >Billing Address</span
                    >
                  </div>

                  <a
                    v-if="billing.property_address"
                    target="_blank"
                    :href="`https://maps.google.com/?q=${billing.property_address}`"
                    class="font-weight-600 font-size-14 color-custom-blue text-decoration-none d-block mb-3 hover-cyan"
                  >
                    {{ billing.property_address }}
                  </a>
                  <em v-else class="text-muted font-size-14 italic d-block mb-3"
                    >No Billing Address</em
                  >

                  <v-divider class="my-3 border-dashed"></v-divider>

                  <div class="d-flex align-center mb-2">
                    <v-avatar size="26" color="green lighten-5" class="mr-2">
                      <v-icon x-small color="green darken-2"
                        >mdi-account</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-700 font-size-14 text-slate-800"
                      >Contact details</span
                    >
                  </div>

                  <template v-if="visitDetail.billing_person">
                    <div
                      v-if="visitDetail.billing_person.display_name"
                      class="font-size-14 text-slate-700 font-weight-500 mb-1"
                    >
                      {{ visitDetail.billing_person.display_name }}
                    </div>
                    <div
                      v-if="visitDetail.billing_person.primary_phone"
                      class="font-size-14 text-slate-700 d-flex align-center mb-1"
                    >
                      <v-icon x-small color="green darken-2" class="mr-2"
                        >mdi-phone</v-icon
                      >
                      <a
                        :href="`tel:${visitDetail.billing_person.primary_phone}`"
                        class="text-slate-700 text-decoration-none"
                      >
                        {{ visitDetail.billing_person.primary_phone }}
                      </a>
                    </div>
                    <div
                      v-if="visitDetail.billing_person.primary_email"
                      class="font-size-14 text-slate-700 d-flex align-center"
                    >
                      <v-icon x-small color="green darken-2" class="mr-2"
                        >mdi-email-outline</v-icon
                      >
                      <a
                        :href="`mailto:${visitDetail.billing_person.primary_email}`"
                        class="text-slate-700 text-decoration-none"
                      >
                        {{ visitDetail.billing_person.primary_email }}
                      </a>
                    </div>
                  </template>
                  <em v-else class="text-muted font-size-14 italic"
                    >No Contact Details</em
                  >
                </v-col>
              </v-row>
            </v-col>

            <!-- Right Half: Contract Info Overview (Soft Light Blue Background) -->
            <v-col cols="12" md="5" class="bg-blue-light pa-4">
              <!-- Key Info List Table -->
              <div class="d-flex flex-column gap-3">
                <!-- Row 1: Contract Number -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 170px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary">mdi-pound</v-icon>
                    </v-avatar>
                    <span class="text-muted font-size-14">Contract number</span>
                  </div>
                  <router-link
                    :to="{
                      name: 'admin.contract.detail',
                      params: {
                        id: visitDetail.id,
                      },
                    }"
                    target="_blank"
                    class="text-decoration-none"
                  >
                    <Barcode
                      dense
                      small
                      :barcode="visitDetail.barcode"
                    ></Barcode>
                  </router-link>
                </div>

                <!-- Row 2: Duration -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 170px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary"
                        >mdi-calendar-range</v-icon
                      >
                    </v-avatar>
                    <span class="text-muted font-size-14">Duration</span>
                  </div>
                  <v-chip
                    small
                    color="cyan lighten-5"
                    class="cyan--text text--darken-3 font-weight-600 px-2"
                    label
                  >
                    {{ formatDate(visitDetail.contract_start) }} to
                    {{ formatDate(visitDetail.contract_end) }}
                    <template v-if="visitDetail.duration_value">
                      ({{ visitDetail.duration_value }}
                      {{ visitDetail.duration_type }})
                    </template>
                  </v-chip>
                </div>

                <!-- Row 3: Sales Representatives -->
                <div
                  class="d-flex align-center"
                  v-if="
                    visitDetail &&
                    visitDetail.sales_representatives &&
                    visitDetail.sales_representatives.full_name
                  "
                >
                  <div class="d-flex align-center" style="width: 170px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary">mdi-account-tie</v-icon>
                    </v-avatar>
                    <span class="text-muted font-size-14"
                      >Sales Representatives</span
                    >
                  </div>
                  <v-chip
                    color="cyan darken-1"
                    class="white--text font-weight-600 px-3"
                    small
                    label
                  >
                    <v-icon x-small left>mdi-account</v-icon>
                    {{ visitDetail.sales_representatives.full_name }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </div>

      <DeleteDialog :delete-dialog="cancelVisitDialog">
        <template v-slot:title>Cancel Confirmation</template>
        <template v-slot:text>
          <h3 class="font-size-16 font-weight-500 delete-text">
            Once you cancel
            <span class="font-weight-700">{{ visitDetail.barcode }}</span
            >, you won't be able to revert this later.<br />Are you sure you
            want to cancel
            <span class="font-weight-700">{{ visitDetail.barcode }}</span>
            ?
          </h3>
        </template>
        <template v-slot:action>
          <v-btn
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="cancelVisitDialog = false"
            >No! Cancel</v-btn
          >
          <v-btn
            color="red lighten-1 white--text"
            v-on:click="cancelVisitConfirm()"
            class="mx-2 custom-grey-border custom-bold-button"
            >Yes! Cancel</v-btn
          >
        </template>
      </DeleteDialog>

      <Dialog :commonDialog="appointmentUpdateDialog">
        <template v-slot:title>Reschedule Appointment</template>
        <template v-slot:body>
          <v-container fluid class="py-0" style="min-height: 22vh">
            <v-row>
              <v-col md="4" class="py-1 font-weight-600"> Date </v-col>
              <v-col md="4" class="py-1 font-weight-600"> Start Time </v-col>
              <v-col md="4" class="py-1 font-weight-600"> End Time </v-col>
              <v-col md="4" class="py-1">
                <Datepicker
                  :defaultDate="updateStartDate"
                  solo
                  v-on:update:date-picker="updateAppointmentDate"
                ></Datepicker>
              </v-col>
              <v-col md="4" class="py-1">
                <v-autocomplete
                  dense
                  color="cyan"
                  filled
                  id="start-time"
                  :items="timeList"
                  v-model.trim="starTimereschdule"
                  placeholder="Start Time"
                  label="Start Time"
                  solo
                  flat
                  item-color="cyan"
                  hide-details
                />
              </v-col>
              <v-col md="4" class="py-1">
                <v-autocomplete
                  dense
                  color="cyan"
                  filled
                  id="end-time"
                  :items="timeList"
                  v-model.trim="endTimereschdule"
                  placeholder="End Time"
                  label="End Time"
                  solo
                  flat
                  item-color="cyan"
                  hide-details
                />
              </v-col>
              <v-col md="12" class="py-2" v-if="false">
                <em
                  >All engineers will be removed from visit#
                  {{ visitDetail.barcode }}</em
                >
              </v-col>
            </v-row>
          </v-container>
        </template>
        <template v-slot:action>
          <v-btn
            :disabled="apptFormLoading || pageLoading"
            :loading="apptFormLoading"
            class="mx-2 custom-grey-border custom-bold-button white--text"
            color="cyan"
            v-on:click="updateAppointment"
          >
            Save
          </v-btn>
          <v-btn
            :disabled="apptFormLoading || pageLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="appointmentUpdateDialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>

      <!-- Create Closer -->
      <template v-if="getPermission('closer:create') && closerDialog">
        <CreateTimeCloser
          v-if="runningTimer.id"
          is-visit-detail
          :ticket-id="ticketId"
          :visit-id="visitId"
          :timesheet-id="runningTimer.id"
          :timesheet="runningTimer.id"
          :visit-detail="visitDetail"
          :closer-dialog="closerDialog"
          :detail="detail"
          v-on:create:ticket-closer="createdTimeCloser"
          v-on:close-dialog="closerDialog = false"
        ></CreateTimeCloser>
      </template>
    </template>
  </Dialog>
</template>

<script>
import moment from "moment-timezone";
import Barcode from "@/view/pages/partials/Barcode.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import CommonMixin from "@/core/plugins/common-mixin";
//import Priority from "@/view/pages/partials/Priority.vue";
import CustomStatus from "@/view/pages/partials/CustomStatus.vue";
import {
  PUT,
  QUERY,
  POST,
  PATCH,
  GET,
} from "@/core/services/store/request.module";
import JwtService from "@/core/services/jwt.service";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import { ServiceFormEventBus } from "@/core/lib/service.form.lib";
import { toNumber } from "lodash";
import { downloadEvent } from "@/core/lib/ics.lib";
import DeleteDialog from "@/view/pages/partials/Delete.vue";
import Datepicker from "@/view/pages/partials/Datepicker.vue";
//import Timepicker from "@/view/pages/partials/Timepicker.vue";
// import VueTimepicker from "vue2-timepicker/src/vue-timepicker.vue";
import CreateTimeCloser from "@/view/pages/job/partials/Create-Ticket-Closer.vue";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  mixins: [CommonMixin],
  props: {
    visitDialog: {
      type: Boolean,
      default: false,
    },
    isCalendar: {
      type: Boolean,
      default: false,
    },
    visit: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    visit: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          this.loadContent();
        }
      },
    },
  },
  data() {
    return {
      moreActions: [],
      statusLoading: false,

      yourFormat: "hh:mm A",
      starTimereschdule: null,
      endTimereschdule: null,
      ticketId: null,
      visitId: null,
      resumeTimerDialog: false,
      assignFormLoading: false,
      assignFormValid: true,
      appointmentUpdateDialog: false,
      timesheetRunningDialog: false,
      apptFormLoading: false,
      allowUpdate: false,
      timerLoading: true,
      timerDialog: false,
      closerDialog: false,
      startTimerDialog: false,
      completeDialog: false,
      updateServiceFormDialog: false,
      assignServiceFormDialog: false,
      allTimerDialog: false,
      timer_remarks: null,
      audioDataURL: null,
      timerFormValid: true,
      timerFormLoading: false,
      recordingStarted: false,
      recordingStopped: false,
      loadingEngineers: false,
      engineerDialog: false,
      apptEngLoading: false,
      runningTimer: new Object(),
      isTimmerRunning: false,
      pageLoading: false,
      detail: new Object(),
      visitDetail: {},
      customer: new Object(),
      contactPerson: new Object(),
      property: new Object(),
      property_contact_person: new Object(),
      billing_contact_person: new Object(),
      billing: new Object(),
      visitNotes: new Object(),
      particuleFieldLoading: false,
      updateDetailDialog: {
        title: false,
        instruction: false,
      },
      updateDetail: {
        title: null,
        instruction: null,
      },
      updateAppointmentFormData: {
        date: null,
        start_time: null,
        end_time: null,
      },
      serviceFormList: new Array(),
      engineerList: new Array(),
      visitEngineer: null,
      userRunningTimers: new Array(),
      timeInterval: null,
      durationInterval: null,
      timerServiceForm: new Array(),
      timeIntervalLimit: 10000,
      timerDuration: {
        hour: "00",
        minute: "00",
        second: "00",
      },
      closer: {
        pdf_url: null,
      },
      assignedTeam: [],
      dbLineItems: [],
      newLineItems: [],
      cancelVisitDialog: false,
      cancelVisitLoading: false,
      cancelVisitBarcode: null,
      lineItemLoading: false,
    };
  },
  methods: {
    cancelVisitConfirm() {
      this.cancelVisitLoading = true;
      this.$store
        .dispatch(PUT, { url: `visit/${this.visitDetail.id}/mark-as-cancel` })
        .then(() => {
          this.loadContent();
          this.$emit("reload", true);
          this.cancelVisitDialog = false;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.cancelVisitLoading = false;
        });
    },
    downloadSchedule() {
      const ticketId = this.visitDetail.ticket.id;
      const token = JwtService.getToken();
      const url = `${this.$apiURL}job/${ticketId}/download-schedule?token=${token}`;
      window.open(url, "_blank");
    },
    updateEngineer() {
      const visitId = this.visitDetail.id;
      const ticketId = this.visitDetail.ticket.id;
      const _this = this;
      if (!_this.assignedTeam.length) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please select an engineer then try again.")
        );
        return false;
      }
      _this.apptEngLoading = true;
      _this.$store
        .dispatch(PUT, {
          url: "job/" + ticketId + "/visit/" + visitId + "/engineer",
          data: {
            engineer: _this.assignedTeam,
          },
        })
        .then(() => {
          _this.engineerList = [];
          _this.$nextTick(() => {
            _this.loadContent();
          });
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.apptEngLoading = false;
        });
    },
    getAvailableEngineers() {
      const _this = this;
      _this.loadingEngineers = true;
      _this.$store
        .dispatch(QUERY, {
          url: "job/available-engineers",
          data: {
            start_date: moment(_this.visitDetail.started_at).format(
              "YYYY-MM-DD"
            ),
            start_time: moment(_this.visitDetail.started_at).format("HH:mm"),
            end_date: moment(_this.visitDetail.finished_at).format(
              "YYYY-MM-DD"
            ),
            end_time: moment(_this.visitDetail.finished_at).format("HH:mm"),
            force: 0,
            all: 1,
          },
        })
        .then(({ data }) => {
          _this.engineerList = data.engineers;
          // _this.engineerDialog = true;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.loadingEngineers = false;
        });
    },
    downloadPDF() {
      if (this.closer.pdf_url) {
        window.open(this.closer.pdf_url, "_blank");
      } else {
        const token = JwtService.getToken();
        const url = `${this.$apiURL}visit/${this.visitDetail.id}/closer/${this.closer.id}/download?token=${token}`;
        window.open(url, "_blank");
      }
    },
    routeToInvoice() {
      const { ticket } = this.visitDetail;

      this.$router.push(
        this.getDefaultRoute("invoice.create", {
          query: {
            customer: this.customer.id,
            property: this.property.id,
            billing: this.billing.id,
            visit: this.visitDetail.id,
            ticket: ticket.id,
          },
        })
      );
    },
    closerServiceFormDialog() {
      const _this = this;
      _this.updateServiceFormDialog = false;
      // _this.getActiveTimer();
      _this.loadContent();
    },
    assignServiceForm() {
      const _this = this;

      if (!_this.$refs.assignServiceForm.validate()) {
        return false;
      }

      _this.assignFormLoading = true;

      _this.$store
        .dispatch(POST, {
          url: `job/${_this.detail.id}/visit/${_this.visit}/service-form/${_this.timerServiceForm}`,
          data: {
            timesheet: _this.runningTimer.id || null,
          },
        })
        .then(() => {
          _this.timerServiceForm = new Array();
          ServiceFormEventBus.$emit("reload:service-form");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.assignFormLoading = false;
          _this.assignServiceFormDialog = false;
          //_this.getActiveTimer();
        });
    },
    assignServiceFormDetail() {
      const _this = this;
      _this.timerLoading = true;

      _this.$store
        .dispatch(QUERY, {
          url: "job/service-forms",
          data: {
            timesheet: _this.runningTimer.id,
            customer: _this.customer.customer,
          },
        })
        .then(({ data }) => {
          _this.serviceFormList = data;
          _this.assignServiceFormDialog = true;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerLoading = false;
        });
    },
    createdTimeCloser() {
      this.closerDialog = false;
      this.$emit("close", true);
      this.$emit("reload", true);
      // this.getActiveTimer();
      this.loadContent();
    },
    updatedServiceForm() {
      this.updateServiceFormDialog = false;
      // this.getActiveTimer();
    },
    timesheetRunningClose() {
      const _this = this;
      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, { url: "running-time-sheet" })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
          _this.getServiceFormList();
        });
    },
    checkRunningTimerStatus() {
      const _this = this;
      _this.timerLoading = true;

      _this.$store
        .dispatch(GET, { url: "running-time-sheet" })
        .then(({ data }) => {
          if (_this.lodash.isEmpty(data) === false) {
            _this.userRunningTimers = data;
            _this.timesheetRunningDialog = true;
          } else {
            _this.getServiceFormList();
          }
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerLoading = false;
        });
    },
    getServiceFormList() {
      const _this = this;
      _this.timerLoading = true;
      _this.$store
        .dispatch(QUERY, {
          url: "job/service-forms",
          data: {
            customer: _this.customer.customer,
          },
        })
        .then(({ data }) => {
          _this.serviceFormList = data;
          _this.startTimerDialog = true;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerLoading = false;
          _this.timesheetRunningDialog = false;
        });
    },
    updateAppointmentDate(param) {
      this.updateAppointmentFormData.date = param;
    },
    updateAppointmentStartTime(param) {
      this.updateAppointmentFormData.start_time = param;
    },
    updateAppointmentEndTime(param) {
      this.updateAppointmentFormData.end_time = param;
    },
    updateAppointment() {
      const _this = this;
      _this.apptFormLoading = true;

      _this.$store
        /* .dispatch(PATCH, {
          url:
            "job/" + _this.detail.id + "/visit/" + _this.visit + "/re-schedule",
          data: {
            date: _this.updateAppointmentFormData.date
              ? moment(_this.updateAppointmentFormData.date).format(
                  "YYYY-MM-DD"
                )
              : moment().format("YYYY-MM-DD"),
            start_time: _this.updateAppointmentFormData.start_time
              ? moment(_this.updateAppointmentFormData.start_time, [
                  "h:mm A",
                ]).format("HH:mm")
              : moment().startOf("day").format("HH:mm"),
            end_time: _this.updateAppointmentFormData.end_time
              ? moment(_this.updateAppointmentFormData.end_time, [
                  "h:mm A",
                ]).format("HH:mm")
              : moment().endOf("day").format("HH:mm"),
          },
        }) */
        .dispatch(PATCH, {
          url:
            "job/" + _this.detail.id + "/visit/" + _this.visit + "/re-schedule",
          data: {
            date: _this.updateAppointmentFormData.date
              ? moment(_this.updateAppointmentFormData.date).format(
                  "YYYY-MM-DD"
                )
              : moment().format("YYYY-MM-DD"),
            end_date: _this.updateAppointmentFormData.end_date
              ? moment(_this.updateAppointmentFormData.end_date).format(
                  "YYYY-MM-DD"
                )
              : moment().format("YYYY-MM-DD"),
            start_time: _this.starTimereschdule
              ? moment(_this.starTimereschdule, ["hh:mm a"]).format("HH:mm")
              : moment().startOf("day").format("HH:mm"),
            end_time: _this.endTimereschdule
              ? moment(_this.endTimereschdule, ["hh:mm a"]).format("HH:mm")
              : moment().endOf("day").format("HH:mm"),
          },
        })
        .then(() => {
          _this.appointmentUpdateDialog = false;
          _this.loadContent();
          _this.$emit("reload-calendar", true);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.apptFormLoading = false;
        });
    },
    startTimer() {
      const _this = this;
      if (!_this.$refs.timerStartForm.validate()) {
        return false;
      }

      _this.timerFormLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url:
            "job/" + _this.detail.id + "/visit/" + _this.visit + "/timer/start",
          data: {
            detail: _this.timer_remarks,
            service_form: _this.timerServiceForm,
          },
        })
        .then(() => {
          _this.$emit("reload", true);
          _this.startTimerDialog = false;
          _this.timerServiceForm = new Array();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerFormLoading = false;
          // _this.getActiveTimer();
          _this.loadContent();
        });
    },
    startTimerWithLater() {
      const _this = this;
      if (_this.isTimmerRunning) {
        _this.closerDialog = true;
      } else {
        _this.timerFormLoading = true;
        _this.$store
          .dispatch(PATCH, {
            url:
              "job/" +
              _this.detail.id +
              "/visit/" +
              _this.visit +
              "/timer/start",
          })
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.$emit("reload", true);
            _this.timerFormLoading = false;
            // _this.getActiveTimer(true);
            _this.loadContent();
          });
      }
    },
    setTimerDuration() {
      const _this = this;
      if (_this.lodash.isEmpty(_this.runningTimer) === false) {
        let startDate = _this.lodash.toString(_this.runningTimer.date);
        if (_this.runningTimer.start_at) {
          startDate = _this.lodash.toString(
            startDate + " " + _this.runningTimer.start_at
          );
        }

        if (startDate && moment(startDate, ["YYYY-MM-DD HH:mm:ss"]).isValid()) {
          startDate = moment(startDate, ["YYYY-MM-DD HH:mm:ss"]);

          let duration = moment.duration(moment().diff(startDate));

          let hours = parseInt(duration.asHours());
          let minutes = parseInt(duration.asMinutes()) % 60;
          let seconds = parseInt(duration.asSeconds()) % 60;

          _this.timerDuration = {
            hour: hours > 0 ? _this.lodash.padStart(hours, 2, "0") : "00",
            minute: minutes > 0 ? _this.lodash.padStart(minutes, 2, "0") : "00",
            second: seconds > 0 ? _this.lodash.padStart(seconds, 2, "0") : "00",
          };
        }
      }
    },
    updateTimerDuration() {
      const _this = this;
      clearInterval(_this.durationInterval);
      _this.durationInterval = setInterval(_this.setTimerDuration, 1000);
    },
    recordingStartedUpdate(param) {
      this.recordingStarted = param;
    },
    recordingStoppedUpdate(param) {
      this.recordingStopped = param;
    },
    stopRunningTimer() {
      const _this = this;

      if (_this.recordingStarted === true && _this.recordingStopped === false) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please stop the audio recorder then try again.")
        );
        return false;
      }

      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url:
            "job/" +
            _this.runningTimer.ticket +
            "/visit/" +
            _this.runningTimer.visit +
            "/timer/" +
            _this.runningTimer.id +
            "/end",
          data: { detail: _this.timer_remarks, audio: _this.audioDataURL },
        })
        .then(() => {
          _this.timerDialog = false;
          _this.timer_remarks = null;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
          // _this.getActiveTimer();
          _this.loadContent();
        });
    },
    markAsComplete() {
      const _this = this;
      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url:
            "job/" +
            _this.detail.id +
            "/visit/" +
            _this.visit +
            "/update-status/1",
        })
        .then(() => {
          _this.loadContent();
          _this.$emit("reload", true);
          _this.completeDialog = false;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
    stopAllTimers() {
      const _this = this;
      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url: "visit/" + _this.visit + "/stop-all-timer",
        })
        .then(() => {
          _this.loadContent();
          _this.$emit("reload", true);
          _this.allTimerDialog = false;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
    checkTimerStatus() {
      const _this = this;
      clearInterval(_this.timeInterval);
      _this.timeInterval = setInterval(
        // _this.getActiveTimer,
        _this.timeIntervalLimit
      );
    },
    /*  getActiveTimer(closer) {
      const _this = this;
      if (_this.visitDetail.status < 4) {
        _this.timerLoading = true;
        _this.$store
          .dispatch(GET, {
            url: "visit/" + _this.visit + "/running",
          })
          .then(({ data }) => {
            if (data && !isEmpty(data)) {
              _this.runningTimer = new Object(data);
              _this.ticketId = data.ticket;
              _this.visitId = data.visit;
            }

            _this.isTimmerRunning = !!data;

            if (_this.isTimmerRunning) {
              _this.resumeTimerDialog = false;
            }

            if (closer) {
              _this.$nextTick(() => {
                _this.closerDialog = true;
              });
            }
          })
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.timerLoading = false;
          });
      }
    }, */
    updateField(type) {
      const _this = this;
      if (_this.canUpdateVisit) {
        if (!_this.allowUpdate) {
          return false;
        }
        _this.onEscParticularField();
        _this.updateDetailDialog[type] = true;
        _this.updateDetail[type] = _this.visitDetail[type];
        setTimeout(function () {
          _this.$refs[type].focus();
        }, 50);
      }
    },
    onEscParticularField() {
      this.updateDetailDialog = {
        title: false,
        instruction: false,
      };
      this.updateDetail = {
        title: null,
        instruction: null,
      };
    },
    patchVisit(data) {
      const _this = this;
      return new Promise((resolve, reject) => {
        try {
          _this.$store
            .dispatch(PATCH, {
              url: "visit/" + _this.visit,
              data,
            })
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(error);
        }
      });
    },
    updateParticularField() {
      const _this = this;
      _this.particuleFieldLoading = true;
      if (_this.canUpdateVisit) {
        _this
          .patchVisit(_this.updateDetail)
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.loadContent();
            _this.particuleFieldLoading = false;
            _this.onEscParticularField();
          });
      }
    },
    resumeTimer() {
      const _this = this;
      _this.timerFormLoading = true;
      _this.$store
        .dispatch(PATCH, { url: "visit/" + _this.visit + "/resume" })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerFormLoading = false;
          // _this.getActiveTimer();
          _this.loadContent();
        });
    },
    downloadVisit() {
      try {
        let startDateArray = moment(this.visitDetail.started_at)
          .format("YYYY-MM-DD")
          .split("-")
          .map((row) => toNumber(row));

        let startTimeArray = moment(this.visitDetail.started_at)
          .format("HH:mm")
          .split(":")
          .map((row) => toNumber(row));

        let endDateArray = moment(this.visitDetail.finished_at)
          .format("YYYY-MM-DD")
          .split("-")
          .map((row) => toNumber(row));

        let endTimeArray = moment(this.visitDetail.finished_at)
          .format("HH:mm")
          .split(":")
          .map((row) => toNumber(row));

        if (startTimeArray[0] == "00") {
          startTimeArray = new Array();
        }

        const visit = {
          start: this.lodash.concat(startDateArray, startTimeArray),
          end: this.lodash.concat(endDateArray, endTimeArray),
          title: this.visitDetail.title,
          description: this.visitDetail.instruction,
          location: this.property.property_address,
          url: process.env.VUE_APP_BASE_URL,
          geo: {
            lat: toNumber(this.property.latitude),
            lon: toNumber(this.property.longitude),
          },
          categories: this.detail.ticket_type,
          status: "CONFIRMED",
          organizer: {
            name: this.detail.added_by.display_name,
            email: this.detail.added_by.user_email,
          },
          attendees: this.visitDetail.engineers.map((row) => {
            if (row.engineer) {
              return {
                name: row.engineer.full_name,
                email: row.engineer.primary_email,
              };
            }
          }),
        };
        downloadEvent(visit);
      } catch (error) {
        ErrorEventBus.$emit("update:error", InitializeError(error));
      }
    },
    getVisitScheduleType() {
      return this.detail ? this.detail.schedule_message : "";
    },
    loadContent() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(GET, {
          url: "contract/" + _this.visit,
        })
        .then(({ data }) => {
          _this.visitDetail = data;
          _this.moreActions = data.more_actions;
          _this.starTimereschdule = moment(_this.visitDetail.started_at).format(
            "hh:mm A"
          );
          _this.endTimereschdule = moment(_this.visitDetail.finished_at).format(
            "hh:mm A"
          );

          _this.closer = data.closer;
          _this.detail = data.ticket;
          _this.customer = data.customer;
          _this.contactPerson = data.contact_person || new Object();
          _this.property = data.property || new Object();
          _this.property_contact_person =
            data.property_contact_person || new Object();
          _this.billing_contact_person =
            data.billing_contact_person || new Object();
          _this.billing = data.billing || new Object();
          _this.visitNotes = data.notes;
          _this.allowUpdate = true;
          if (_this.visitDetail.status === 4) {
            _this.allowUpdate = false;
            clearInterval(_this.timeInterval);
            clearInterval(_this.durationInterval);
          }

          if (
            _this.visitDetail.engineers &&
            _this.visitDetail.engineers.length
          ) {
            _this.assignedTeam = _this.visitDetail.engineers.map(
              (row) => row.engineer
            );
          }
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          //_this.getActiveTimer();
          _this.pageLoading = false;
        });
    },
  },
  components: {
    Dialog,
    // Priority,
    Barcode,
    Datepicker,
    // Timepicker,
    // VueTimepicker,
    CreateTimeCloser,
    DeleteDialog,
    CustomStatus,
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 70);
    },
    updateStartDate() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.started_at).format("YYYY-MM-DD");
      }
      return null;
    },
    updateStartTime() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.started_at).format("hh:mm A");
      }
      return null;
    },
    updateEndTime() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.finished_at).format("hh:mm A");
      }
      return null;
    },
    canUpdateVisit() {
      if (this.visitDetail && this.visitDetail.status != 1) {
        return false;
      }
      return this.getPermission("visit:update");
    },
    getAppointmentText() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        let date = this.formatDate(this.visitDetail.started_at);
        let startTime = moment(this.visitDetail.started_at).format("hh:mm A");
        let endTime = moment(this.visitDetail.finished_at).format("hh:mm A");
        return date + " " + startTime + " - " + endTime;
      }
      return null;
    },
    getScheduleType() {
      if (this.detail) {
        if (this.detail.type === 1) {
          return "One-Off Job";
        }
        if (this.detail.type === 2) {
          return "Recurring Job";
        }
      }
      return null;
    },
  },
};
</script>

<style scoped>
.border-slate {
  border-color: #e2e8f0 !important;
}
.border-right-slate {
  border-right: 1px solid #e2e8f0 !important;
}
.border-right-dashed-sm {
  border-right: 1px dashed #cbd5e1 !important;
}
@media (max-width: 600px) {
  .border-right-dashed-sm {
    border-right: none !important;
    border-bottom: 1px dashed #cbd5e1 !important;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }
}
.border-dashed {
  border-style: dashed !important;
}
.bg-slate-50 {
  background-color: #f8fafc !important;
}
.bg-blue-light {
  background-color: #eef6fc !important;
}
.hover-cyan:hover {
  color: #00b4d8 !important;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
</style>
